import { useLayoutEffect, useState } from "react";

export interface ViewportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const sameRect = (a: ViewportRect | null, b: ViewportRect): boolean =>
  !!a &&
  a.left === b.left &&
  a.top === b.top &&
  a.width === b.width &&
  a.height === b.height;

/// Where `ref` sits in the viewport while `active`, re-measured whenever it
/// resizes or anything scrolls or resizes around it — so a fixed overlay keyed
/// to it follows the page instead of staying where it was first placed.
export function useElementRect(
  ref: React.RefObject<HTMLElement | null>,
  active: boolean,
): ViewportRect | null {
  const [rect, setRect] = useState<ViewportRect | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!active || !node) {
      setRect(null);
      return;
    }
    const measure = () => {
      const { left, top, width, height } = node.getBoundingClientRect();
      const next = { left, top, width, height };
      setRect((prev) => (sameRect(prev, next) ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [ref, active]);

  return rect;
}
